/*
 * ap-govx-popup.js — GOVX ID verification without leaving the site.
 *
 * Paulo (2026-10-07): "with govx dont take people away from the website —
 * launch a pop up window or javascript on the page so it doesnt take people
 * away from where they are at."
 *
 * Behaviour
 *  1. Any click on a GOVX verification trigger is handled here, in the
 *     CAPTURE phase on `window`, so it runs before the GOVX app's own
 *     handler (govx.js rewrites our anchor to href="#" target="popup" and
 *     binds its own opener — we stop that so only ONE window is ever made).
 *  2. We open a centred, sized popup window. The shopper's page is never
 *     navigated, never replaced, never detached.
 *  3. An on-page overlay stays behind the popup telling the shopper their
 *     cart/page is still here, with a "Continue shopping" dismiss and a
 *     manual re-open link. That is the "javascript on the page" half.
 *  4. If the browser blocks the popup we do NOT swallow the click silently:
 *     the overlay shows a direct, user-activated "Open verification" link.
 *
 * Trigger matching is deliberately broad and does not depend on GOVX class
 * names staying the same:
 *   - any element carrying `data-ap-govx-url` (our own product-page block)
 *   - any <a> whose href resolves to govx.com or a subdomain
 *   - any GOVX app wrapper that contains such a link
 * The canonical URL `https://auth.govx.com/shopify/verify?shop=<domain>`
 * 302s into exactly the same signed OAuth flow the app's JS builds, so
 * taking over the click does not bypass anything.
 */
(function () {
  'use strict';

  var WINDOW_NAME = 'govxIdVerify';
  var WANT_W = 520;
  var WANT_H = 780;

  var APP_SELECTORS = [
    '[data-ap-govx-url]',
    '[data-govx-id]',
    '.govx-id-v2-wrapper',
    '.govx-id-v2-full-wrapper',
    '.govx-id-v2-full',
    '.govx-id-v2-slim',
    '.govx-id-full-wrapper',
    '.govx-id-v2-cta',
    '.alkaline-product-govx__button'
  ].join(',');

  var popup = null;
  var pollTimer = null;
  var overlay = null;
  var lastUrl = '';

  function isGovxUrl(href) {
    if (!href || href === '#') return false;
    try {
      return /^([^.]+\.)*govx\.com$/i.test(new URL(href, window.location.href).hostname);
    } catch (e) {
      return /(^https?:)?\/\/([^/]*\.)?govx\.com\//i.test(href);
    }
  }

  /* Returns the URL to verify with, or null if this click is not ours. */
  function resolveTarget(startEl) {
    var el = startEl;
    while (el && el.nodeType === 1) {
      var explicit = el.getAttribute && el.getAttribute('data-ap-govx-url');
      if (explicit) return explicit;
      if (el.tagName === 'A' && isGovxUrl(el.getAttribute('href'))) return el.href;
      if (el.matches && el.matches(APP_SELECTORS)) {
        var inner = el.querySelector && el.querySelector('[data-ap-govx-url],a[href]');
        if (inner) {
          var iu = inner.getAttribute('data-ap-govx-url');
          if (iu) return iu;
          if (isGovxUrl(inner.getAttribute('href'))) return inner.href;
        }
      }
      el = el.parentNode;
    }
    return null;
  }

  // ---------------------------------------------------------------- overlay
  function buildOverlay() {
    var o = document.createElement('div');
    o.className = 'ap-govx-overlay';
    o.setAttribute('role', 'dialog');
    o.setAttribute('aria-modal', 'false');
    o.setAttribute('aria-label', 'GOVX ID verification in progress');
    o.innerHTML =
      '<div class="ap-govx-overlay__panel">' +
        '<p class="ap-govx-overlay__eyebrow">GOVX ID verification</p>' +
        '<p class="ap-govx-overlay__title">Verifying in a secure window</p>' +
        '<p class="ap-govx-overlay__body">We opened GOVX ID in a separate window so you stay right here. ' +
          'Finish verifying there and come back &mdash; your cart and this page are untouched.</p>' +
        '<div class="ap-govx-overlay__actions">' +
          '<button type="button" class="ap-govx-overlay__btn" data-ap-govx-dismiss>Continue shopping</button>' +
          '<a class="ap-govx-overlay__link" data-ap-govx-reopen target="_blank" rel="noopener noreferrer" href="#">Window didn&rsquo;t open? Open it here</a>' +
        '</div>' +
      '</div>';
    var style = document.createElement('style');
    style.textContent =
      '.ap-govx-overlay{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;' +
      'padding:20px;background:rgba(5,7,10,.62);-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);}' +
      '.ap-govx-overlay__panel{max-width:420px;width:100%;box-sizing:border-box;background:#0f1511;border:1px solid rgba(133,193,45,.35);' +
      'border-radius:16px;padding:26px 24px;text-align:left;box-shadow:0 24px 60px rgba(0,0,0,.45);}' +
      '.ap-govx-overlay__eyebrow{margin:0 0 8px;color:#85c12d;font-size:11px;font-weight:800;letter-spacing:.16em;text-transform:uppercase;}' +
      '.ap-govx-overlay__title{margin:0 0 10px;color:#fff;font-size:20px;font-weight:800;line-height:1.25;}' +
      '.ap-govx-overlay__body{margin:0 0 18px;color:rgba(242,245,247,.82);font-size:14px;line-height:1.55;}' +
      '.ap-govx-overlay__actions{display:flex;flex-direction:column;gap:10px;}' +
      '.ap-govx-overlay__btn{appearance:none;border:0;cursor:pointer;width:100%;padding:13px 18px;border-radius:8px;' +
      'background:#85c12d;color:#0d1117;font-size:15px;font-weight:800;font-family:inherit;}' +
      '.ap-govx-overlay__btn:hover{background:#74ab24;}' +
      '.ap-govx-overlay__link{color:rgba(255,255,255,.62);font-size:13px;text-decoration:underline;text-align:center;}' +
      '.ap-govx-overlay__link:hover{color:#fff;}';
    o.appendChild(style);
    o.addEventListener('click', function (e) {
      if (e.target === o || e.target.hasAttribute('data-ap-govx-dismiss')) hideOverlay();
    });
    return o;
  }

  function showOverlay(url, blocked) {
    if (!overlay) overlay = buildOverlay();
    var link = overlay.querySelector('[data-ap-govx-reopen]');
    if (link) {
      link.href = url;
      link.textContent = blocked
        ? 'Your browser blocked the window — open verification'
        : 'Window didn’t open? Open it here';
    }
    var title = overlay.querySelector('.ap-govx-overlay__title');
    var body = overlay.querySelector('.ap-govx-overlay__body');
    if (blocked && title && body) {
      title.textContent = 'Popup blocked by your browser';
      body.innerHTML = 'Use the link below to start GOVX ID verification. You will come straight back to this page afterwards.';
    }
    if (!overlay.isConnected) document.body.appendChild(overlay);
    document.documentElement.style.overflow = 'hidden';
  }

  function hideOverlay() {
    if (overlay && overlay.isConnected) overlay.parentNode.removeChild(overlay);
    document.documentElement.style.overflow = '';
  }

  // ------------------------------------------------------------------ popup
  function watchClose() {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = setInterval(function () {
      if (!popup || popup.closed) {
        clearInterval(pollTimer);
        pollTimer = null;
        popup = null;
        hideOverlay();
        document.dispatchEvent(new CustomEvent('ap:govx-closed'));
        // The cart is the only surface whose contents depend on verification.
        if (/^\/cart(\/|$)/.test(window.location.pathname)) window.location.reload();
      }
    }, 600);
  }

  function openPopup(url) {
    if (popup && !popup.closed) { try { popup.focus(); } catch (e) {} return true; }
    var availW = (window.screen && screen.availWidth) || 1024;
    var availH = (window.screen && screen.availHeight) || 768;
    var w = Math.round(Math.max(320, Math.min(WANT_W, availW - 40)));
    var h = Math.round(Math.max(420, Math.min(WANT_H, availH - 80)));
    var left = Math.round(Math.max(0, (availW - w) / 2 + (window.screenX || 0)));
    var top = Math.round(Math.max(0, (availH - h) / 2 + (window.screenY || 0)));
    var features = 'popup=yes,toolbar=no,menubar=no,location=no,status=no,scrollbars=yes,resizable=yes' +
      ',width=' + w + ',height=' + h + ',left=' + left + ',top=' + top;
    var win = null;
    try { win = window.open(url, WINDOW_NAME, features); } catch (e) { win = null; }
    if (!win || win.closed || typeof win.closed === 'undefined') return false;
    popup = win;
    try { popup.focus(); } catch (e) { /* cross-origin focus may throw */ }
    watchClose();
    return true;
  }

  function onClick(event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    // never hijack clicks inside our own overlay
    if (overlay && overlay.contains(event.target)) return;

    var url = resolveTarget(event.target);
    if (!url) return;
    lastUrl = url;

    // Take the click away from govx.js so it cannot open a second window.
    event.preventDefault();
    event.stopImmediatePropagation();

    showOverlay(url, !openPopup(url));
  }

  // window-capture beats document-capture, which beats govx.js's own binding
  window.addEventListener('click', onClick, true);
  document.addEventListener('click', onClick, true);
})();
